import { useMemo, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Modal } from "../components/Modal";
import type { LatestPlanSnapshot, MealLog, NutritionEstimate } from "../types";
import { todayMeals } from "../utils";

interface NutritionPageProps {
  snapshot: LatestPlanSnapshot | null;
  meals: MealLog[];
  estimateMeal: (text: string) => Promise<NutritionEstimate>;
  addMeal: (meal: Omit<MealLog, "client_meal_id" | "created_at">) => Promise<MealLog>;
  deleteMeal: (id: string) => Promise<void>;
  clearMeals: () => Promise<void>;
  confirmDelete: (kind: "meal" | "all") => Promise<boolean>;
  onEditProfile: () => void;
  onError: (message: string) => void;
}

const emptyMeal = () => ({ meal_type: "Breakfast", text: "", calories: 0, protein: 0, carbs: 0, fat: 0 });

export function NutritionPage({ snapshot, meals, estimateMeal, addMeal, deleteMeal, clearMeals, confirmDelete, onEditProfile, onError }: NutritionPageProps) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyMeal);
  const [estimating, setEstimating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [estimateStatus, setEstimateStatus] = useState("");
  const today = useMemo(() => todayMeals(meals), [meals]);
  const totals = today.reduce((sum, meal) => ({
    calories: sum.calories + meal.calories,
    protein: sum.protein + meal.protein,
    carbs: sum.carbs + meal.carbs,
    fat: sum.fat + meal.fat,
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  const nutrition = snapshot?.plan.nutrition_plan;

  async function estimate() {
    if (!form.text.trim()) return;
    setEstimating(true);
    try {
      const result = await estimateMeal(form.text.trim());
      setForm({ ...form, ...result.nutrition });
      setEstimateStatus(result.low_confidence
        ? "Low-confidence estimate. Review the values before saving."
        : `Recognized: ${result.recognized.join(", ") || "meal"}.`);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Unable to estimate nutrition.");
    } finally {
      setEstimating(false);
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    try {
      await addMeal({ ...form, logged_at: new Date().toISOString() });
      setForm(emptyMeal());
      setEstimateStatus("");
      setOpen(false);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Unable to save meal.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!(await confirmDelete("meal"))) return;
    try { await deleteMeal(id); } catch (caught) { onError(caught instanceof Error ? caught.message : "Unable to delete meal."); }
  }

  async function clear() {
    if (!meals.length || !(await confirmDelete("all"))) return;
    try { await clearMeals(); } catch (caught) { onError(caught instanceof Error ? caught.message : "Unable to clear meals."); }
  }

  return (
    <section className="route active-route">
      <header className="page-header actions-header">
        <div><p className="eyebrow">FUEL</p><h1>Nutrition Today</h1></div>
        <div className="header-actions"><button className="button secondary" type="button" onClick={() => void clear()}><Icon name="trash" />Clear logs</button><button className="button primary" type="button" onClick={() => setOpen(true)}><Icon name="plus" />Log food</button></div>
      </header>

      <div className="nutrition-layout">
        <section className="card">
          {nutrition ? (
            <><p className="eyebrow">DAILY TARGETS</p><h2>Macro progress</h2><div className="macro-list">
              <Macro label="Calories" current={totals.calories} target={nutrition.daily_calories} unit="kcal" />
              <Macro label="Protein" current={totals.protein} target={nutrition.macros?.protein_grams || 0} unit="g" />
              <Macro label="Carbs" current={totals.carbs} target={nutrition.macros?.carbs_grams || 0} unit="g" />
              <Macro label="Fat" current={totals.fat} target={nutrition.macros?.fat_grams || 0} unit="g" />
            </div></>
          ) : <div className="empty-state"><div><h2>Nutrition is not enabled</h2><p>Enable it in your training profile and rebuild the plan.</p><button className="button primary" type="button" onClick={onEditProfile}>Enable nutrition</button></div></div>}
        </section>

        <section className="card"><div className="section-title"><div><p className="eyebrow">LOGGED</p><h2>Today</h2></div></div><div className="meal-list">
          {today.length ? today.map((meal) => <article className="meal-row" key={meal.client_meal_id}><div><span className="status-label">{meal.meal_type}</span><strong>{meal.text || "Logged meal"}</strong><small>{Math.round(meal.calories)} kcal · P{Math.round(meal.protein)} C{Math.round(meal.carbs)} F{Math.round(meal.fat)}</small></div><button className="icon-button danger-text" type="button" onClick={() => void remove(meal.client_meal_id)} aria-label="Delete meal"><Icon name="trash" /></button></article>) : <div className="empty-inline">No food logged today.</div>}
        </div></section>

        <section className="card wide-card"><p className="eyebrow">MEAL PLAN</p><h2>Planned meals</h2>
          {nutrition?.meal_schedule?.length ? <><div className="planned-grid">{nutrition.meal_schedule.map((meal) => <article key={`${meal.time}-${meal.meal}`}><span>{meal.time}</span><strong>{meal.meal}</strong><p>{meal.description}</p><small>{meal.approx_calories} kcal</small></article>)}</div>{nutrition.restrictions_applied?.length ? <div className="restriction-list">{nutrition.restrictions_applied.map((item) => <span className="pill warm" key={item}>{item}</span>)}</div> : null}</> : <div className="empty-inline">No planned meals in the current plan.</div>}
        </section>
      </div>

      <Modal open={open} title="Add a meal" eyebrow="LOG FOOD" onClose={() => setOpen(false)}>
        <form className="form-stack" onSubmit={save}>
          <label className="field">Meal<select value={form.meal_type} onChange={(event) => setForm({ ...form, meal_type: event.target.value })}><option>Breakfast</option><option>Lunch</option><option>Dinner</option><option>Snack</option><option>Other</option></select></label>
          <label className="field">What did you eat?<textarea required rows={3} value={form.text} onChange={(event) => setForm({ ...form, text: event.target.value })} placeholder="e.g. cereal and orange juice" /></label>
          <button className="button secondary" type="button" disabled={estimating} onClick={() => void estimate()}>{estimating ? "Estimating…" : "Estimate nutrition"}</button>
          {estimateStatus ? <div className="notice">{estimateStatus}</div> : null}
          <div className="field-grid">
            {(["calories", "protein", "carbs", "fat"] as const).map((key) => <label className="field" key={key}>{key[0].toUpperCase() + key.slice(1)}{key !== "calories" ? " (g)" : ""}<input type="number" min="0" step={key === "calories" ? "1" : "0.1"} required value={form[key]} onChange={(event) => setForm({ ...form, [key]: Number(event.target.value) })} /></label>)}
          </div>
          <footer className="modal-footer"><button className="button text" type="button" onClick={() => setOpen(false)}>Cancel</button><button className="button primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Add meal"}</button></footer>
        </form>
      </Modal>
    </section>
  );
}

function Macro({ label, current, target, unit }: { label: string; current: number; target: number; unit: string }) {
  const percent = target ? Math.min(100, Math.round(current / target * 100)) : 0;
  return <div className="macro-row"><div><strong>{label}</strong><span>{Math.round(current)} / {Math.round(target)} {unit}</span></div><div className="bar"><i style={{ width: `${percent}%` }} /></div></div>;
}
